import { motion } from "framer-motion";
import { useNavigate } from "react-router-dom";
import {
  Calendar, Sparkles, PartyPopper, Rocket, Clock, Gift,
  ChevronRight, MapPin, Zap,
} from "lucide-react";
import { BirthdayCelebration } from "@/components/shared/BirthdayCelebration";

/**
 * Contextual card on the homepage that escalates in urgency as birthday approaches.
 * Replaces the auto-redirect to the countdown page.
 *
 * Tiers:
 *   > 30 days  — small nudge ("Start planning!")
 *   11-30 days — medium card ("Your birthday is coming!")
 *   1-10 days  — dominant hero with countdown number
 *   0 days     — full celebration takeover
 *   -1 to -7   — post-birthday window ("Still time to claim!")
 */
export default function BirthdayEscalationCard({ days, userName, userColor, hasItinerary }) {
  const navigate = useNavigate();
  if (days == null || (days > 180 && days >= 0)) return null;

  const color = userColor || "#0ea5e9";

  // ═══ POST-BIRTHDAY WINDOW (-1 to -7) ═══
  if (days < 0) {
    const daysAfter = Math.abs(days);
    const remaining = 7 - daysAfter;
    return (
      <div className="px-4 mb-4" data-testid="escalation-post-bday">
        <motion.div
          initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }}
          className="rounded-2xl overflow-hidden relative text-white shadow-lg"
          style={{ background: `linear-gradient(135deg, #f59e0b, #ef4444)` }}
        >
          <div className="p-5">
            <div className="flex items-center gap-3">
              <div className="flex-1">
                <div className="flex items-center gap-2 mb-1">
                  <Clock className="w-4 h-4 text-yellow-200" />
                  <span className="text-xs font-bold text-white/80 uppercase tracking-wider">Birthday Window Open</span>
                </div>
                <p className="text-lg font-black">{remaining} {remaining === 1 ? "day" : "days"} left to claim!</p>
                <p className="text-xs text-white/60 mt-1">Your birthday deals are still redeemable — don't miss out</p>
              </div>
              <motion.div
                animate={{ scale: [1, 1.1, 1] }}
                transition={{ duration: 1.5, repeat: Infinity }}
                className="w-14 h-14 rounded-2xl bg-white/20 flex items-center justify-center"
              >
                <Gift className="w-7 h-7 text-white" />
              </motion.div>
            </div>
            <div className="flex gap-2 mt-4">
              <button onClick={() => navigate("/my-deals")} className="flex-1 py-2.5 bg-white text-slate-800 rounded-xl text-xs font-bold shadow-lg flex items-center justify-center gap-1.5" data-testid="escalation-claim-btn">
                <Gift className="w-3.5 h-3.5" />Claim My Deals
              </button>
              {hasItinerary && (
                <button onClick={() => navigate("/my-itinerary")} className="py-2.5 px-4 bg-white/15 text-white rounded-xl text-xs font-bold" data-testid="escalation-itinerary-btn">
                  <MapPin className="w-3.5 h-3.5" />
                </button>
              )}
            </div>
          </div>
        </motion.div>
      </div>
    );
  }

  // ═══ DAY 0 — Full Celebration ═══
  if (days === 0) {
    return (
      <div className="px-4 mb-4" data-testid="escalation-day0">
        <BirthdayCelebration name={userName} />
        <motion.div
          initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}
          className="rounded-3xl p-6 text-center text-white shadow-2xl overflow-hidden relative"
          style={{ background: `linear-gradient(135deg, ${color}, #f472b6, #fbbf24)` }}
        >
          <div className="absolute inset-0 opacity-10">
            {[...Array(10)].map((_, i) => (
              <motion.div key={i} className="absolute w-2 h-2 bg-white rounded-full"
                style={{ left: `${(i * 19 + 5) % 100}%`, top: `${(i * 23 + 10) % 100}%` }}
                animate={{ opacity: [0.2, 1, 0.2], scale: [0.5, 1.5, 0.5] }}
                transition={{ duration: 2 + (i % 3), repeat: Infinity, delay: i * 0.3 }}
              />
            ))}
          </div>
          <div className="relative z-10">
            <PartyPopper className="w-10 h-10 mx-auto mb-3 text-white/90" />
            <h2 className="text-2xl font-black">HAPPY BIRTHDAY!</h2>
            <p className="text-sm text-white/80 mt-1">{userName}, today is YOUR day</p>
            <div className="flex gap-2 mt-4 justify-center">
              {hasItinerary && (
                <button onClick={() => navigate("/my-itinerary")} className="px-5 py-2.5 bg-white text-slate-800 rounded-2xl text-sm font-bold shadow-lg" data-testid="escalation-go-itinerary">
                  Launch Itinerary
                </button>
              )}
              <button onClick={() => navigate("/countdown")} className="px-5 py-2.5 bg-white/20 text-white rounded-2xl text-sm font-bold backdrop-blur-sm" data-testid="escalation-go-countdown">
                {hasItinerary ? "View Prep" : "See What's Waiting"}
              </button>
            </div>
          </div>
        </motion.div>
      </div>
    );
  }

  // ═══ 1-10 DAYS — Dominant Hero ═══
  if (days <= 10) {
    const fontSize = Math.max(4, 7 - days * 0.3);
    return (
      <div className="px-4 mb-4" data-testid="escalation-urgent">
        <motion.div
          initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}
          className="rounded-3xl overflow-hidden relative text-white shadow-xl"
          style={{ background: `linear-gradient(135deg, ${color}dd, ${color}88)` }}
        >
          <div className="absolute inset-0 opacity-10">
            {[...Array(8)].map((_, i) => (
              <motion.div key={i} className="absolute w-1.5 h-1.5 bg-white rounded-full"
                style={{ left: `${(i * 21 + 3) % 100}%`, top: `${(i * 17 + 5) % 100}%` }}
                animate={{ opacity: [0.2, 0.8, 0.2] }}
                transition={{ duration: 3, repeat: Infinity, delay: i * 0.4 }}
              />
            ))}
          </div>
          <div className="relative z-10 p-5">
            <div className="flex items-center justify-between">
              <div className="flex-1">
                <div className="flex items-center gap-2 mb-1">
                  {days <= 3 ? <Zap className="w-4 h-4 text-yellow-300" /> : <Rocket className="w-4 h-4 text-white/70" />}
                  <span className="text-xs font-bold text-white/70 uppercase tracking-wider">
                    {days === 1 ? "TOMORROW!" : days <= 3 ? "Final Stretch!" : "Birthday Countdown"}
                  </span>
                </div>
                <p className="text-sm text-white/60 mt-1">
                  {hasItinerary ? "Your plan is ready — review it now" : "Time to build your birthday plan!"}
                </p>
              </div>
              <motion.div
                animate={{ scale: [1, 1.08, 1] }}
                transition={{ duration: 2, repeat: Infinity }}
                className="text-right"
              >
                <p className="font-black text-white leading-none drop-shadow-lg" style={{ fontSize: `${fontSize}rem` }}>{days}</p>
                <p className="text-[10px] text-white/50 font-semibold mt-1">{days === 1 ? "day left" : "days left"}</p>
              </motion.div>
            </div>
            <div className="flex gap-2 mt-4">
              <button onClick={() => navigate(hasItinerary ? "/my-itinerary" : "/my-itinerary")} className="flex-1 py-2.5 bg-white text-slate-800 rounded-xl text-xs font-bold shadow-lg flex items-center justify-center gap-1.5" data-testid="escalation-plan-btn">
                <MapPin className="w-3.5 h-3.5" />{hasItinerary ? "View Itinerary" : "Build Your Plan"}
              </button>
              <button onClick={() => navigate("/countdown")} className="py-2.5 px-4 bg-white/15 text-white rounded-xl text-xs font-bold backdrop-blur-sm flex items-center gap-1.5" data-testid="escalation-prep-btn">
                <Gift className="w-3.5 h-3.5" />Prep
              </button>
            </div>
          </div>
        </motion.div>
      </div>
    );
  }

  // ═══ 11-30 DAYS — Medium Card ═══
  if (days <= 30) {
    return (
      <div className="px-4 mb-4" data-testid="escalation-approaching">
        <motion.div
          initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }}
          onClick={() => navigate("/my-itinerary")}
          className="rounded-2xl p-4 cursor-pointer border shadow-md transition-all hover:shadow-lg active:scale-[0.98]"
          style={{ borderColor: color + "40", background: `linear-gradient(135deg, ${color}10, ${color}08)` }}
        >
          <div className="flex items-center gap-3">
            <div className="w-11 h-11 rounded-2xl flex items-center justify-center flex-shrink-0" style={{ backgroundColor: color + "20" }}>
              <Calendar className="w-5 h-5" style={{ color }} />
            </div>
            <div className="flex-1 min-w-0">
              <h3 className="text-sm font-bold text-slate-800">Your birthday is in {days} days</h3>
              <p className="text-xs text-slate-500 mt-0.5">
                {hasItinerary ? "Review your plan and make adjustments" : "Start building your birthday plan — sign up early for the best deals"}
              </p>
            </div>
            <div className="flex items-center gap-1 text-xs font-semibold flex-shrink-0" style={{ color }}>
              <span>{days}d</span>
              <ChevronRight className="w-4 h-4" />
            </div>
          </div>
        </motion.div>
      </div>
    );
  }

  // ═══ 31-180 DAYS — Small Nudge ═══
  return (
    <div className="px-4 mb-3" data-testid="escalation-distant">
      <motion.div
        initial={{ opacity: 0 }} animate={{ opacity: 1 }}
        onClick={() => navigate("/my-itinerary")}
        className="rounded-xl px-4 py-3 bg-slate-100 border border-slate-200 cursor-pointer hover:bg-slate-50 transition-colors flex items-center gap-3 active:scale-[0.98]"
      >
        <Sparkles className="w-4 h-4 text-slate-400 flex-shrink-0" />
        <span className="text-xs text-slate-500 flex-1">
          <strong className="text-slate-700">{days} days</strong> until your birthday — {hasItinerary ? "review your plan" : "start planning!"}
        </span>
        <ChevronRight className="w-4 h-4 text-slate-300" />
      </motion.div>
    </div>
  );
}
